import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import FilePicker from '../../file-picker'

FileSelectionStep.propTypes = {
  files: PropTypes.array.isRequired,
  extractedIdentifiers: PropTypes.array.isRequired,
  identifierLabel: PropTypes.string.isRequired,
  onFilesChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.array,
  maxFiles: PropTypes.number,
  maxFileSize: PropTypes.string,
}

export default function FileSelectionStep({
  files,
  extractedIdentifiers,
  identifierLabel,
  onFilesChange,
  onNext,
  acceptedFileTypes,
  maxFiles,
  maxFileSize,
}) {
  const { t } = useTranslation()

  const hasFiles = files.length > 0
  const hasAnyIdentifiers = extractedIdentifiers.length > 0
  const allHaveIdentifiers =
    hasAnyIdentifiers && extractedIdentifiers.every((id) => id && id.value)

  return (
    <Box>
      <Typography variant="body1" gutterBottom>
        {t(
          'components.multi-document-import-dialog.file-selection-description',
          'Select PDF files to import. Each file should contain documents for one item.'
        )}
      </Typography>

      <Box sx={{ mt: 3, mb: 3 }}>
        <FilePicker
          onChange={onFilesChange}
          initialFiles={files}
          acceptedFileTypes={acceptedFileTypes}
          maxFiles={maxFiles}
          maxFileSize={maxFileSize}
        />
      </Box>

      {hasFiles && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t(
              'components.multi-document-import-dialog.selected-files',
              'Selected Files'
            )}{' '}
            ({files.length})
          </Typography>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.filename', 'Filename')}</TableCell>
                  <TableCell>{identifierLabel}</TableCell>
                  <TableCell align="right">
                    {t('common.size', 'Size')}
                  </TableCell>
                  <TableCell align="center">
                    {t('common.status', 'Status')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file, index) => {
                  const identifier = extractedIdentifiers[index]
                  const hasIdentifier = identifier && identifier.value

                  return (
                    <TableRow key={index}>
                      <TableCell>{file.name}</TableCell>
                      <TableCell>
                        {hasIdentifier ? (
                          <Chip
                            label={identifier.value}
                            size="small"
                            color="primary"
                          />
                        ) : (
                          <Typography variant="caption" color="error">
                            {t(
                              'components.multi-document-import-dialog.no-identifier',
                              'Not found'
                            )}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </TableCell>
                      <TableCell align="center">
                        {hasIdentifier ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <ErrorIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {!allHaveIdentifiers && hasAnyIdentifiers && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
              {t(
                'components.multi-document-import-dialog.some-files-missing-identifiers',
                'Some files could not have their identifiers extracted. They will be marked as invalid in the next step.'
              )}
            </Typography>
          )}

          {!hasAnyIdentifiers && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {t(
                'components.multi-document-import-dialog.no-identifiers-extracted',
                'No identifiers could be extracted from any files. Please check the filenames.'
              )}
            </Typography>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={onNext}
              disabled={!hasAnyIdentifiers}
            >
              {t('common.next', 'Next')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
